/**
 * Hook for managing Schedule Gantt View editing
 *
 * Handles:
 * - Loading trip data for time range
 * - Converting to temporal format
 * - Tracking schedule changes
 * - Applying changes to database
 * - Error handling and user feedback
 */

import { useCallback, useState } from "react";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { PlacementService } from "../../../../services/PlacementService";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import {
  convertScheduleChangesToDB,
  convertTripsToTemporalFormat,
  ScheduleChangeWithTrip,
  TripWithCoverage,
} from "../../TemporalTest/scheduledDataConversion";
import { ParkingRequest, Placement, Zone } from "../../TemporalTest/types";
import { ScheduleChange } from "../components/SpotOccupancyGantt";

interface UseScheduleGanttEditOptions {
  rampId: string;
  rampShapes: Shape[];
  rampDepth: number;
  zones?: Zone[];
  placementService: PlacementService;
  onApplied?: () => void; // Callback after successful apply
}

interface ScheduleGanttData {
  aircraft: ParkingRequest[];
  placement: Placement;
  zones: Zone[];
  startTime: Date;
  horizonHours: number;
  tripMap: Map<string, string>; // aircraftId -> tripId
}

export function useScheduleGanttEdit(options: UseScheduleGanttEditOptions) {
  const { rampId, rampShapes, rampDepth, zones, placementService, onApplied } =
    options;
  const snackbar = useSnackbar();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [data, setData] = useState<ScheduleGanttData | null>(null);
  const [pendingChanges, setPendingChanges] = useState<ScheduleChange[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load trip data for the given time range
   */
  const loadData = useCallback(
    async (startTime: Date, endTime: Date) => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch placements for time range (filtered by ramp)
        const placements = await placementService.getPlacementsInTimeRange(
          startTime,
          endTime,
          rampId,
          "ramp"
        );

        // Build TripWithCoverage data from placements
        // Group placements by trip_id to build coverage arrays
        const tripMap = new Map<string, TripWithCoverage>();

        for (const placement of placements) {
          // Placements are already filtered by rampId in the query

          const tripId = placement.trip_id;

          // Get or create trip entry
          let trip = tripMap.get(tripId);
          if (!trip) {
            trip = {
              trip_id: tripId,
              tenant_id: placement.tenant_id || tripId,
              tail_number: placement.tail_number || "Unknown",
              aircraft_manufacturer: placement.aircraft_make,
              aircraft_model: placement.aircraft_model,
              wingspan: placement.wingspan,
              aircraft_length: placement.length,
              arrival_date: placement.start_time,
              departure_date: placement.end_time,
              fbo_id: "",
              status: "active" as const,
              created_at: placement.created_at || new Date().toISOString(),
              updated_at: placement.updated_at || new Date().toISOString(),
              version: 1,
              coverage: [],
            };
            tripMap.set(tripId, trip);
          }

          // Add coverage segment
          trip.coverage.push({
            start_time: placement.start_time,
            end_time: placement.end_time,
            x: placement.x || null,
            y: placement.y || null,
            angle: placement.angle || null,
            ramp_id: placement.ramp_id,
            hangar_id: placement.hangar_id,
          });
        }

        // Convert to temporal format
        const trips = Array.from(tripMap.values());
        const temporal = convertTripsToTemporalFormat(
          trips,
          rampShapes,
          rampDepth,
          startTime,
          rampId
        );

        // Calculate horizon hours
        const horizonMs = endTime.getTime() - startTime.getTime();
        const horizonHours = Math.ceil(horizonMs / (1000 * 60 * 60));

        // Build result
        setData({
          aircraft: temporal.aircraft,
          placement: temporal.placement,
          zones: zones || [],
          startTime: startTime,
          horizonHours,
          tripMap: temporal.tripMap,
        });

        setIsLoading(false);
      } catch (err) {
        console.error("[useScheduleGanttEdit] Failed to load data:", err);
        setError(err instanceof Error ? err.message : "Failed to load data");
        setIsLoading(false);
      }
    },
    [rampId, rampShapes, rampDepth, zones, placementService]
  );

  /**
   * Track schedule changes from Gantt
   */
  const handleScheduleChanged = useCallback((changes: ScheduleChange[]) => {
    setPendingChanges(changes);
  }, []);

  /**
   * Apply pending changes to database
   */
  const applyChanges = useCallback(async () => {
    if (!data || pendingChanges.length === 0) {
      snackbar.notify({
        text: "No changes to apply",
        severity: "info",
      });
      return;
    }

    setIsApplying(true);

    try {
      // Enhance changes with trip IDs
      const changesWithTrips: ScheduleChangeWithTrip[] = pendingChanges.map(
        (change) => ({
          ...change,
          tripId: data.tripMap.get(change.aircraftId),
        })
      );

      // Convert changes to database format
      const segmentsByTrip = convertScheduleChangesToDB(
        changesWithTrips,
        rampShapes,
        rampDepth,
        rampId,
        data.startTime
      );

      // Apply each trip's changes
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (const tripEntry of Array.from(segmentsByTrip.entries())) {
        const [tripId, segments] = tripEntry;
        try {
          await placementService.rebuildTripCoverage(tripId, segments);
          successCount++;
        } catch (err) {
          console.error(
            `[useScheduleGanttEdit] Failed to apply changes for trip ${tripId}:`,
            err
          );
          errorCount++;
          errors.push(tripId);
        }
      }

      // Show results
      if (errorCount === 0) {
        snackbar.notify({
          text: `Successfully applied changes for ${successCount} trip${
            successCount !== 1 ? "s" : ""
          }`,
          severity: "success",
        });
      } else if (successCount > 0) {
        snackbar.notify({
          text: `Applied ${successCount}/${
            successCount + errorCount
          } changes (${errorCount} errors)`,
          severity: "warning",
        });
      } else {
        snackbar.notify({
          text: "Failed to apply changes",
          severity: "error",
        });
      }

      // Clear pending changes
      setPendingChanges([]);
      setIsApplying(false);

      // Callback to refresh parent view
      if (onApplied) {
        onApplied();
      }
    } catch (err) {
      console.error("[useScheduleGanttEdit] Failed to apply changes:", err);
      snackbar.notify({
        text: `Failed to apply changes: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
        severity: "error",
      });
      setIsApplying(false);
    }
  }, [
    data,
    pendingChanges,
    rampShapes,
    rampDepth,
    rampId,
    placementService,
    snackbar,
    onApplied,
  ]);

  /**
   * Reset pending changes
   */
  const resetChanges = useCallback(() => {
    setPendingChanges([]);
  }, []);

  return {
    // State
    isLoading,
    isApplying,
    data,
    pendingChanges,
    error,

    // Actions
    loadData,
    handleScheduleChanged,
    applyChanges,
    resetChanges,
  };
}
